/* ============================================================================
 * MenuBar — pinned to the top of the desktop
 * ----------------------------------------------------------------------------
 * Structural piece of the retro-desktop language: the menu bar is part of the
 * brand, so it is always present rather than hidden behind a window.
 *
 * Real functions only:
 *   • Desks menu    launches any registered executable (13 entries)
 *   • Skin menu     picks between the three design languages by name
 *   • clock         real time, tabular numerals
 *   • session chip  wallet state
 * ========================================================================= */

import { useEffect, useRef, useState } from 'react';
import { APPS, PENDING_APPS } from '../apps/registry';
import { SKINS, SKIN_LABEL, SKIN_NOTE, type SkinController } from '../hooks/useSkin';
import { useWallet } from '../hooks/useWallet';

interface Props {
  onLaunch: (appId: string) => void;
  skin: SkinController;
}

type OpenMenu = 'desks' | 'skin' | null;

const PANEL_STYLE: React.CSSProperties = {
  position: 'absolute',
  top: 'var(--ui-row-h)',
  zIndex: 200,
  background: 'var(--ui-surface)',
  border: 'var(--ui-bw) solid var(--ui-border)',
  borderRadius: 'var(--ui-radius-ctl)',
  boxShadow: 'var(--ui-bevel-rest)',
  padding: 4,
  minWidth: 230,
};

const ITEM_STYLE: React.CSSProperties = {
  display: 'block',
  width: '100%',
  textAlign: 'left',
  padding: '3px 6px',
};

function useClock(): { time: string; date: string } {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(t);
  }, []);
  return {
    time: `${String(now.getHours()).padStart(2, '0')}:${String(
      now.getMinutes(),
    ).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`,
    date: now.toLocaleDateString('en-CA'),
  };
}

export function MenuBar({ onLaunch, skin }: Props) {
  const { time, date } = useClock();
  const w = useWallet();
  const [open, setOpen] = useState<OpenMenu>(null);
  const ref = useRef<HTMLElement | null>(null);

  // Click-away closes the open menu.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(null);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const toggle = (m: Exclude<OpenMenu, null>) =>
    setOpen((o) => (o === m ? null : m));

  return (
    <header id="menubar" ref={ref} aria-label="Menu bar">
      <span className="menubar__brand">
        <img
          className="menubar__mark"
          src="/logos/supercompute-mark.png"
          alt=""
          aria-hidden="true"
        />
        SUPERCOMPUTE
      </span>

      <button
        type="button"
        className="menubar__menu"
        data-open={open === 'desks'}
        aria-haspopup="menu"
        aria-expanded={open === 'desks'}
        onClick={() => toggle('desks')}
      >
        Desks ▾
      </button>

      {open === 'desks' && (
        <div role="menu" style={{ ...PANEL_STYLE, left: 200, maxHeight: 420, overflowY: 'auto' }}>
          {APPS.map((a) => (
            <button
              key={a.id}
              type="button"
              role="menuitem"
              className="menubar__menu"
              style={ITEM_STYLE}
              title={a.blurb}
              onClick={() => {
                onLaunch(a.id);
                setOpen(null);
              }}
            >
              {a.label}
              {a.component === null && (
                <span className="tag tag--warn" style={{ marginLeft: 6 }}>
                  not built
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      <button
        type="button"
        className="menubar__menu"
        data-open={open === 'skin'}
        aria-haspopup="menu"
        aria-expanded={open === 'skin'}
        title={`Panel: ${SKIN_LABEL[skin.skin]}`}
        onClick={() => toggle('skin')}
      >
        Panel: {SKIN_LABEL[skin.skin]} ▾
      </button>

      {open === 'skin' && (
        <div role="menu" style={{ ...PANEL_STYLE, left: 290, minWidth: 320 }}>
          {SKINS.map((s) => (
            <button
              key={s}
              type="button"
              role="menuitemradio"
              aria-checked={skin.skin === s}
              className="menubar__menu"
              style={{
                ...ITEM_STYLE,
                padding: '6px 8px',
                borderBottom: '1px solid var(--ui-border-dim)',
                textAlign: 'left',
                whiteSpace: 'normal',
              }}
              onClick={() => {
                skin.setSkin(s);
                setOpen(null);
              }}
            >
              <span
                style={{
                  display: 'block',
                  color:
                    skin.skin === s ? 'var(--ui-accent)' : 'var(--ui-text)',
                  fontWeight: 700,
                }}
              >
                {skin.skin === s ? '● ' : '○ '}
                {SKIN_LABEL[s]}
              </span>
              <span
                style={{
                  display: 'block',
                  fontSize: 10,
                  lineHeight: 1.45,
                  textTransform: 'none',
                  letterSpacing: 0,
                  color: 'var(--ui-muted-text)',
                }}
              >
                {SKIN_NOTE[s]}
              </span>
            </button>
          ))}
        </div>
      )}

      <span className="menubar__spacer" />

      <span className="menubar__status" title="Wallet session">
        {w.isConnected ? `● ${w.handle}` : '○ guest'}
      </span>
      <span className="menubar__clock">
        {date} {time}
      </span>
    </header>
  );
}

/** Surfaced in the Desks menu; exported so the count stays honest. */
export const PENDING_COUNT = PENDING_APPS.length;
